import ResponsiveImage from "@/components/ResponsiveImage";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Building2, Check, Home, Landmark, MapPin } from "lucide-react";
import PageNotFound from "@/lib/PageNotFound";
import Seo, { breadcrumbSchema, faqSchema, organizationSchema, serviceSchema } from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaSection from "@/components/sections/CtaSection";
import FaqSection from "@/components/sections/FaqSection";
import RecoBrand from "@/components/RecoBrand";
import ReviewExcerpt from "@/components/ReviewExcerpt";
import ImageHero from "@/components/ImageHero";
import { HomeRecoBar } from "@/components/home/HomeStreamlined";
import { COMPANY, IMAGES, REVIEWS } from "@/data/company";
import { getLocation, getPublishedLocalServices, LOCATIONS } from "@/data/locations";
import { getLocalPageMeta, LOCAL_SERVICE_CONFIG } from "@/data/localSeo";
import { LOCAL_DECISION_SUPPORT } from "@/data/localDecisionSupport";
import { SERVICES } from "@/data/services";
import { GUIDES } from "@/data/guides";

const SERVICE_VISUALS = {
  taklaggare: [IMAGES.hero, IMAGES.teamRoofInstallation, IMAGES.rowhouseTile],
  takbesiktning: [IMAGES.inspection, IMAGES.ridgeTile, IMAGES.roofStructureCraft],
  takservice: [IMAGES.rooferWork, IMAGES.eavesCraft, IMAGES.roofDetailWork],
  takmalning: [IMAGES.roofPainting, IMAGES.darkVillaGable, IMAGES.blackTileDetail],
  platslageri: [IMAGES.villaMetal, IMAGES.workerCutting, IMAGES.roofDetailWork],
  serviceavtal: [IMAGES.brfAerial, IMAGES.brfTop, IMAGES.socialView],
  takskottning: [IMAGES.snowRemoval, IMAGES.roofByWater, IMAGES.brfProjectDroneNew],
};

const CUSTOMER_PATHS = [
  { icon: Home, title: "Villa & radhus", text: "Få hjälp att förstå takets skick och välja rätt nästa steg.", to: "/privatkund/" },
  { icon: Building2, title: "BRF & företag", text: "Skapa underlag, plan och en tydlig väg genom takprojektet.", to: "/brf-foretag/" },
  { icon: Landmark, title: "Offentlig verksamhet", text: "Samla dokumentation, samordning och rätt beslutsunderlag.", to: "/offentlig/" },
];

const ROOFING_PLANNING = {
  stockholm: {
    title: "Planera takrenoveringen från första bedömning till överlämning.",
    intro: "TISAB arbetar med tak för villaägare, bostadsrättsföreningar och fastighetsägare i Stockholm. Börja med att avgränsa behovet: en skada som behöver lagas, ett tak som behöver underhåll eller en omläggning. Därefter går det att ta ställning till material, omfattning och genomförande.",
    items: [
      ["Beskriv fastigheten", "Ange adress, fastighetstyp, takmaterial och kända problem. Bilder på taket och synliga skador hjälper oss att avgöra vilket ytterligare underlag som behövs."],
      ["Ta med åtkomst och boende", "Berätta om trånga passager, gård, entréer och pågående verksamhet. Det ger ett bättre underlag för planering av ställning, leveranser och information till boende."],
      ["Jämför samma omfattning", "Kontrollera hur offerten hanterar underlag, ytskikt, plåtdetaljer, avvattning och slutkontroll. Då blir skillnader mellan alternativen lättare att förstå."],
    ],
    faq: { q: "Kan TISAB hjälpa både villaägare och BRF i Stockholm?", a: "Ja. För en villa utgår vi från husets tak och dina behov. För en BRF eller större fastighet behöver även antal byggnader, underlag för styrelsens beslut, åtkomst och boendekommunikation tas med i planeringen." },
    link: { to: "/brf-foretag/", label: "Planera ett takprojekt för BRF eller fastighet" },
  },
  lidingo: {
    title: "Takbyte och plåtarbeten på Lidingö – samla hela uppdraget.",
    intro: `TISAB har sin företagsadress på ${COMPANY.address} på Lidingö och har arbetat med tak sedan ${COMPANY.foundedYear}. Vi hjälper villaägare, föreningar och fastighetsägare med bedömning, takbyte, service och plåtarbeten. Du kan börja med att skicka bilder och beskriva vad du vill få hjälp med.`,
    items: [
      ["Börja med takets skick", "Behöver du veta om taket kan lagas eller måste läggas om? Beskriv vad du sett och vilken fråga du vill få besvarad. Vid en bokad besiktning kommer vi överens om omfattning och dokumentation.", "/takbesiktning/lidingo/", "Takbesiktning på Lidingö"],
      ["Tak och plåt i samma planering", "Vid ett takbyte behöver beslag, skorsten, takfot och avvattning fungera med det nya taket. TISAB har egen plåtverkstad och kan planera måttanpassade detaljer tillsammans med takarbetet.", "/platslageri/lidingo/", "Plåtslageri på Lidingö"],
      ["Planera nästa kontakt", `Ring ${COMPANY.phone} eller skicka en förfrågan. Vi går igenom vad som behöver kompletteras och om ett platsbesök behövs. Kontakta oss gärna i förväg om du vill besöka oss.`],
    ],
    faq: { q: "Kan takbyte och plåtarbeten samordnas på Lidingö?", a: "Ja. TISAB har egen plåtverkstad och arbetar med både tak och måttanpassade plåtdetaljer. Vilka beslag, anslutningar och delar av avvattningen som behöver åtgärdas avgränsas i bedömningen och offerten." },
    link: { to: "/platslageri/", label: "Läs om vår plåtverkstad och våra plåtarbeten" },
  },
  nacka: {
    title: "Ett tydligt underlag för taket i Nacka.",
    intro: "För ett takuppdrag i Nacka börjar TISAB med adressen och fastighetens förutsättningar. Beskriv om frågan gäller en villa, ett radhus eller en föreningsfastighet och om det finns en känd skada. Du behöver inte ha valt takmaterial eller bestämt att hela taket ska bytas innan du kontaktar oss.",
    items: [
      ["Visa takets utgångsläge", "Skicka översiktsbilder och beskriv det du har sett, exempelvis vattenmärken, skadade pannor eller slitna anslutningar. Ange om någon åtgärd redan har gjorts."],
      ["Ta med hela taklösningen", "Vid ett takbyte behöver underlag, ytskikt, genomföringar, avvattning och plåt vägas samman. Materialvalet ska fungera med takets konstruktion och förutsättningar."],
      ["Beskriv uppdragets ramar", "Ange önskad tidplan, hur fastigheten används och vem som ansvarar för beslutet. För en BRF är tidigare besiktningsunderlag och planerade underhållsåtgärder värdefulla."],
    ],
    faq: { q: "Vad behöver ni för att bedöma ett takuppdrag i Nacka?", a: "Börja med adress, fastighetstyp, bilder och en beskrivning av behovet. Takets material, ungefärliga ålder och tidigare besiktningsunderlag är bra att skicka med om du har dem. TISAB återkommer om kompletteringar eller platsbesök behövs." },
    link: { to: "/takbesiktning/", label: "Se vad en takbesiktning kan ge svar på" },
  },
  taby: {
    title: "Takrenovering i Täby – laga, underhålla eller byta?",
    intro: "För villaägare och föreningar i Täby är ett tydligt åtgärdsval en bra början. TISAB hjälper till att bedöma om behovet gäller en begränsad reparation, löpande service eller ett större takbyte. Takets ålder är en uppgift i underlaget, men den behöver vägas samman med material, synligt skick och tidigare åtgärder.",
    items: [
      ["Laga ett avgränsat fel", "En skadad panna, ett beslag eller en lokal läcka kan vara ett serviceärende. Orsaken behöver undersökas, särskilt om felet återkommer. Beskriv när problemet märks och vilka lagningar som redan gjorts.", "/takservice/taby/", "Takservice i Täby"],
      ["Utred ett osäkert takskick", "Om det är oklart vilka delar som är uttjänta är en bedömning nästa steg. Be om besked om synliga brister, vad som kan behållas och vilka delar som kräver ytterligare undersökning.", "/takbesiktning/taby/", "Takbesiktning i Täby"],
      ["Planera en större omläggning", "När behovet gäller både ytskikt och underlag behöver offerten avgränsa rivning, underarbete, nytt material och plåt. Jämför också hur tillkommande skador och slutkontroll hanteras.", "/byta-tak/", "Så planerar vi ett takbyte"],
    ],
    faq: { q: "Måste ett äldre tak i Täby alltid bytas helt?", a: "Nej. Ålder i sig avgör inte omfattningen. Skick, material, underlag och kända skador behöver bedömas. I vissa fall kan en riktad åtgärd räcka, medan andra tak behöver läggas om. Offerten ska beskriva den åtgärd som föreslås." },
    link: { to: "/kunskapsbank/nar-ar-det-dags-att-byta-tak/", label: "Läs guiden om när taket behöver bytas" },
  },
  danderyd: {
    title: "Takläggare i Danderyd för villa, radhus och BRF",
    intro: "TISAB hjälper villaägare, radhusägare och bostadsrättsföreningar i Danderyd med bedömning, takbyte, service och plåtarbeten. När ett tak ska förnyas behöver materialvalet hänga ihop med konstruktion, underlag och anslutningar. Börja därför med takets nuvarande skick och vilka delar du vill bevara eller förändra.",
    items: [
      ["Behålla takets karaktär", "Berätta om du vill behålla material, kulör och takets linjer. Skilj frågan om pannor eller plåt kan återanvändas från frågan om underlaget behöver bytas. Delarna behöver bedömas var för sig.", "/takbesiktning/danderyd/", "Bedöm det befintliga taket"],
      ["Jämföra ett nytt material", "Vid byte av material behöver konstruktion, lutning och produktens anvisningar kontrolleras. Be om alternativ med samma underarbete och tydlig redovisning av vilka detaljer som måste ändras.", "/takmaterial/", "Jämför tegel, betong, plåt och papp"],
      ["Samordna anslutningar och beslag", "Takfönster, skorstenar, ränndalar och takfot kan kräva måttanpassade lösningar. TISAB:s egen plåtverkstad gör det möjligt att samordna detaljerna med resten av taket.", "/platslageri/danderyd/", "Plåtarbeten i Danderyd"],
    ],
    extraFaq: [{ q: "Vilka fastigheter hjälper en takläggare som TISAB med i Danderyd?", a: "TISAB tar emot förfrågningar för villor, radhus och bostadsrättsföreningar i Danderyd. Skicka adress, fastighetstyp och bilder på taket så bedömer vi nästa steg." }],
    faq: { q: "Kan jag byta takmaterial vid ett takbyte i Danderyd?", a: "Det kan vara möjligt, men materialet måste passa takets konstruktion och lutning. Beskriv vilket material du har och vad du vill ändra. Eventuella krav för ändringen behöver också klarläggas innan arbetet bestäms." },
    link: { to: "/kunskapsbank/valja-takmaterial/", label: "Jämför plåt, papp, tegel och betongpannor" },
  },
  solna: {
    title: "Gör takprojektet möjligt att planera och förankra.",
    intro: "I Solna tar TISAB emot förfrågningar från BRF, fastighetsägare, företag och privatkunder. För en förening eller större fastighet behöver det tekniska behovet bli begripligt för dem som fattar beslutet. Börja med takets skick, kända problem och vad ni vill ha svar på inför budget eller upphandling.",
    items: [
      ["Samla styrelsens underlag", "Tidigare besiktningsprotokoll, uppgifter om antal byggnader och planerade åtgärder gör det lättare att avgränsa uppdraget. Beskriv också vem som är kontaktperson för fastigheten."],
      ["Planera runt fastigheten", "Åtkomst, entréer, pågående verksamhet och information till boende behöver tas med tidigt. Ange sådana förutsättningar redan i förfrågan för att underlätta planeringen."],
      ["Följ upp efter åtgärden", "Be om tydlig redovisning av vad som utförts och vilka behov som återstår. Vid återkommande underhåll kan ett serviceavtal ge en struktur för kontroll och rapportering."],
    ],
    faq: { q: "Hur börjar en BRF i Solna ett takprojekt med TISAB?", a: "Utse en kontaktperson och skicka adress, antal byggnader, kända problem och eventuellt besiktningsunderlag. Beskriv om styrelsen behöver bedöma takets skick, ta fram budget eller få offert på en avgränsad åtgärd." },
    link: { to: "/kunskapsbank/takrenovering-for-brf/", label: "Ta fram beslutsunderlag för styrelsen" },
  },
};

const NEIGHBOR_DANDERYD = {
  stockholm: "Har du fastigheten norr om Stockholm? TISAB tar också uppdrag för villor, radhus och BRF som",
  uppsala: "Ska taket bytas på en fastighet närmare Stockholm? Läs om vårt arbete som",
};

export default function LocationPage({ serviceSlug }) {
  const { locationSlug } = useParams();
  const location = getLocation(locationSlug);
  const service = LOCAL_SERVICE_CONFIG[serviceSlug];
  const allowedServices = location ? getPublishedLocalServices(location.slug) : [];

  if (!location || !service) return <PageNotFound embedded />;
  if (!allowedServices.includes(serviceSlug)) return <PageNotFound embedded />;

  const meta = getLocalPageMeta(serviceSlug, location);
  const decisionSupport = LOCAL_DECISION_SUPPORT[serviceSlug];
  const globalService = SERVICES.find((item) => item.path === service.globalPath);
  const relatedGuides = (globalService?.relatedGuides || []).map((slug) => GUIDES.find((guide) => guide.slug === slug)).filter(Boolean);
  const serviceIndex = Object.keys(LOCAL_SERVICE_CONFIG).indexOf(serviceSlug);
  const locationIndex = LOCATIONS.findIndex((item) => item.slug === location.slug);
  const variant = Math.abs(serviceIndex + locationIndex) % 3;
  const [heroImage, contextImage, detailImage] = SERVICE_VISUALS[serviceSlug] || SERVICE_VISUALS.taklaggare;
  const roofingPlanning = serviceSlug === "taklaggare" ? ROOFING_PLANNING[location.slug] : null;
  const selectedReview = REVIEWS[(locationIndex + serviceIndex + 7) % REVIEWS.length];

  const nearbyLocations = location.nearby
    .map((slug) => LOCATIONS.find((item) => item.slug === slug))
    .filter(Boolean)
    .filter((item) => getPublishedLocalServices(item.slug).includes(serviceSlug));
  const relatedServices = allowedServices
    .filter((slug) => slug !== serviceSlug)
    .slice(0, 6)
    .map((slug) => ({ slug, ...LOCAL_SERVICE_CONFIG[slug] }));

  const faq = [
    decisionSupport.faq,
    ...(location.slug === "lidingo" ? [{ q: "Var finns TISAB på Lidingö?", a: `Vår företagsadress är ${COMPANY.fullAddress}. Ring ${COMPANY.phone} eller kontakta oss via formuläret. Kontakta oss i förväg om du vill besöka oss.` }] : location.faq),
    ...(roofingPlanning ? [roofingPlanning.faq, ...(roofingPlanning.extraFaq || [])] : []),
    {
      q: `Vad kostar ${service.shortLabel.toLowerCase()} i ${location.name}?`,
      a: "Priset beror på takets storlek, material, skick, åtkomst och omfattning. Skicka adress, bilder och en kort beskrivning så kan TISAB avgränsa arbetet och ta fram rätt nästa steg."
    },
    {
      q: `Hur börjar jag med ${service.shortLabel.toLowerCase()} i ${location.name}?`,
      a: "Börja med fastigheten: adress, fastighetstyp och vad du har sett. Du behöver inte själv avgöra den tekniska lösningen innan första kontakten."
    }
  ];

  const crumbs = [
    { name: "Områden", path: "/omraden/" },
    ...(serviceSlug === "taklaggare" ? [] : [{ name: location.name, path: `/taklaggare/${location.slug}/` }]),
    { name: meta.h1, path: meta.path }
  ];
  const schema = [
    organizationSchema(),
    serviceSchema(meta.path, meta.h1, meta.metaDescription, location.name),
    breadcrumbSchema(meta.path, crumbs),
    faqSchema(faq)
  ].filter(Boolean);

  return (
    <>
      <Seo title={meta.title} description={meta.metaDescription} path={meta.path} schema={schema} image={heroImage} />
      <Breadcrumbs items={crumbs} />

      <ImageHero
        image={heroImage}
        mobileImage={contextImage}
        alt="Takarbete från TISAB:s verksamhet"
        eyebrow={`TISAB · ${location.region}`}
        title={service.label}
        accent={`${location.slug === "lidingo" ? "på" : "i"} ${location.name}.`}
        description={roofingPlanning ? meta.metaDescription : service.promise}
        primaryAction={{ label: "Få en första bedömning", to: "/kontakta-oss/#offert" }}
        secondaryAction={{ label: COMPANY.phone, href: COMPANY.phoneHref, event: "phone_click" }}
        facts={[
          { value: "2009", label: "Etablerat" },
          { value: "Egen", label: "Plåtverkstad" },
          { value: location.name, label: "Serviceområde" },
        ]}
        showTrust={false}
        imagePosition="center 45%"
      />
      <HomeRecoBar />

      <section className={`section-pad overflow-hidden ${variant === 1 ? "bg-[#dfeee6]" : "bg-[#e8f3ed]"}`}>
        <div className={`container-tisab grid gap-12 lg:items-center xl:gap-20 ${variant === 2 ? "lg:grid-cols-[.52fr_.48fr]" : "lg:grid-cols-[.46fr_.54fr]"}`}>
          <div className={variant === 2 ? "lg:order-2" : ""}>
            <p className="eyebrow">{service.shortLabel} · {location.name}</p>
            <h2 className="mt-3 font-heading text-4xl font-extrabold leading-[.95] text-midnight sm:text-6xl">
              Börja i taket.<br /><span className="text-tisab">Inte i fackspråket.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-foreground/68">{location.intro}</p>
            <p className="mt-4 text-base leading-relaxed text-foreground/70">{location.context}</p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {service.needs.map((need) => (
                <div key={need} className="flex min-h-[64px] items-center gap-3 border border-midnight/10 bg-white px-4 py-3 text-sm font-semibold text-midnight">
                  <Check className="h-4 w-4 shrink-0 text-tisab" /> {need}
                </div>
              ))}
            </div>
          </div>
          <figure className={`relative overflow-hidden ${variant === 2 ? "lg:order-1" : ""}`}>
            <ResponsiveImage src={contextImage} alt="Takarbete och detaljer från TISAB:s verksamhet" className="aspect-[4/5] w-full object-cover" loading="lazy" />
            <figcaption className="absolute inset-x-4 bottom-4 border-l-4 border-tisab bg-[#075c3c]/88 p-5 text-sm font-semibold text-white backdrop-blur-sm">
              {service.lead}
            </figcaption>
          </figure>
        </div>
      </section>

      {roofingPlanning && (
        <section className="section-pad bg-white">
          <div className="container-tisab">
            <p className="eyebrow">Inför ditt takprojekt · {location.name}</p>
            <h2 className="mt-3 max-w-4xl font-heading text-3xl font-extrabold leading-tight text-midnight sm:text-5xl">{roofingPlanning.title}</h2>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-foreground/70">{roofingPlanning.intro}</p>
            <div className="mt-8 grid gap-px overflow-hidden border border-midnight/10 bg-midnight/10 md:grid-cols-3">
              {roofingPlanning.items.map(([title, text, to, label]) => (
                <article key={title} className="bg-parchment p-6 sm:p-8">
                  <h3 className="font-heading text-xl font-extrabold text-midnight">{title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-foreground/70">{text}</p>
                  {to && <Link to={to} className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-tisab underline underline-offset-4">{label}<ArrowRight className="h-4 w-4 shrink-0" /></Link>}
                </article>
              ))}
            </div>
            <Link to={roofingPlanning.link.to} className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-tisab">
              {roofingPlanning.link.label} <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
          </div>
        </section>
      )}

      {variant === 1 ? <CustomerPaths location={location} /> : <Process location={location} service={service} />}

      <section className="section-pad bg-[#edf7f1]">
        <div className="container-tisab grid gap-10 lg:grid-cols-[.6fr_.4fr]">
          <div>
            <p className="eyebrow">{service.label} · {location.name}</p>
            <h2 className="mt-3 max-w-3xl font-heading text-3xl font-extrabold leading-tight text-midnight sm:text-4xl">{decisionSupport.heading}</h2>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-foreground/75">{decisionSupport.text}</p>
            {serviceSlug === "taklaggare" && (
              <p className="mt-5 text-base leading-relaxed text-foreground/75">
                Planerar du ett takbyte? Läs om <Link to="/byta-tak/" className="font-semibold text-tisab underline underline-offset-4">arbetet med att byta tak</Link> och
                {" "}<Link to="/pris-pa-tak/" className="font-semibold text-tisab underline underline-offset-4">vad som påverkar priset på ett takbyte</Link> innan du begär offert.
              </p>
            )}
            {serviceSlug === "taklaggare" && NEIGHBOR_DANDERYD[location.slug] && (
              <p className="mt-5 text-base leading-relaxed text-foreground/75">
                {NEIGHBOR_DANDERYD[location.slug]} <Link to="/taklaggare/danderyd/" className="font-semibold text-tisab underline underline-offset-4">takläggare i Danderyd</Link>.
              </p>
            )}
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
              {relatedGuides.map((guide) => (
                <Link key={guide.slug} to={`/kunskapsbank/${guide.slug}/`} className="inline-flex items-center gap-2 text-sm font-bold text-tisab underline underline-offset-4">
                  {guide.h1} <ArrowRight className="h-4 w-4 shrink-0" />
                </Link>
              ))}
            </div>
          </div>
          <aside className="border border-midnight/10 bg-white p-6 sm:p-8">
            <h3 className="font-heading text-xl font-bold text-midnight">Ta med i din förfrågan</h3>
            <ul className="mt-5 space-y-4">
              {decisionSupport.checklist.map((item) => (
                <li key={item} className="flex gap-3 text-sm leading-relaxed text-foreground/75"><Check className="mt-0.5 h-4 w-4 shrink-0 text-tisab" />{item}</li>
              ))}
            </ul>
            <Link to="/kontakta-oss/#offert" className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-tisab">Beskriv ditt tak i {location.name}<ArrowRight className="h-4 w-4 shrink-0" /></Link>
          </aside>
        </div>
      </section>

      {variant === 1 ? <Process location={location} service={service} /> : null}

      <section className="overflow-hidden bg-[#087a4d] text-white">
        <div className="container-tisab grid lg:grid-cols-[.42fr_.58fr]">
          <figure className="relative min-h-[390px] lg:min-h-[540px]">
            <ResponsiveImage src={detailImage} alt="Takyta och detaljer från TISAB:s takarbeten" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#064b34]/55 to-transparent" />
          </figure>
          <div className="flex flex-col justify-center px-0 py-12 sm:py-16 lg:px-14">
            <p className="eyebrow !text-[#d6f8e5]">Kundernas erfarenheter av TISAB</p>
            <div className="mt-4 flex items-start gap-5">
              <ResponsiveImage src={COMPANY.reco.badge} alt={`Reco – rekommenderat företag ${COMPANY.reco.recommendedYears} år i rad`} className="h-24 w-24 shrink-0 object-contain" loading="lazy" />
              <div>
                <RecoBrand inverse className="[&_img]:!h-8" />
                <strong className="mt-5 block font-heading text-5xl font-extrabold">{COMPANY.reco.rating.toLocaleString("sv-SE")} / 5</strong>
                <span className="text-sm text-white/78">{COMPANY.reco.count} publicerade omdömen</span>
                <p className="mt-2 text-xs leading-relaxed text-white/75">Kontrollerat {COMPANY.reco.checked}</p>
              </div>
            </div>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/80">Omdömena gäller TISAB:s verksamhet som helhet.</p>
            <ReviewExcerpt review={selectedReview} className="mt-8 border-l-2 border-[#9ce4bd] pl-5" textClassName="mt-4 max-w-xl font-heading text-2xl font-bold leading-snug" labelClassName="mt-4 text-xs leading-relaxed text-white/80" iconClassName="h-5 w-5 text-[#d6f8e5]" />
            <a href={COMPANY.social.reco} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#d6f8e5]">Läs omdömena <ArrowRight className="h-4 w-4" /></a>
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#e8f3ed]">
        <div className="container-tisab">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="eyebrow">Fler vägar för taket</p>
              <h2 className="mt-3 font-heading text-3xl font-extrabold text-midnight sm:text-5xl">Relaterat i {location.name}</h2>
            </div>
            <Link to={service.globalPath} className="inline-flex items-center gap-2 text-sm font-bold text-tisab">
              Om {service.shortLabel.toLowerCase()} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-8 grid gap-px overflow-hidden border border-midnight/10 bg-midnight/10 sm:grid-cols-2 lg:grid-cols-3">
            {relatedServices.map((item) => (
              <Link key={item.slug} to={`/${item.slug}/${location.slug}/`} className="group bg-white p-6 transition-colors hover:bg-[#e8f3ed]">
                <span className="text-[9px] font-bold uppercase tracking-[.14em] text-tisab">{location.name}</span>
                <h3 className="mt-3 font-heading text-xl font-extrabold text-midnight">{item.label}</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground/70">{item.promise}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-tisab">Läs mer <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {nearbyLocations.length > 0 && (
        <section className="border-y border-midnight/10 bg-white py-10">
          <div className="container-tisab">
            <p className="text-sm font-semibold text-midnight">Samma hjälp i närliggande områden</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {nearbyLocations.map((item) => (
                <Link key={item.slug} to={`/${serviceSlug}/${item.slug}/`} className="inline-flex items-center gap-2 border border-midnight/10 bg-parchment px-4 py-2.5 text-sm font-medium text-midnight transition-colors hover:border-tisab hover:text-tisab">
                  <MapPin className="h-4 w-4" /> {service.label} {item.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <FaqSection faq={faq} title={`Vanliga frågor om ${service.shortLabel.toLowerCase()} i ${location.name}`} />
      <CtaSection title={`Vad behöver taket i ${location.name}?`} text="Skicka adress, bilder och en kort beskrivning. TISAB återkommer med ett relevant nästa steg." primaryLabel="Beskriv taket" />
    </>
  );
}

function Process({ location, service }) {
  const steps = [
    ["01", "Beskriv fastigheten", `Adress i ${location.name}, bilder och vad du vill få löst.`],
    ["02", "Få rätt bedömning", "TISAB avgör vilket underlag eller platsbesök som behövs."],
    ["03", "Planera åtgärden", `Omfattning och nästa steg för ${service.shortLabel.toLowerCase()} blir tydliga.`],
    ["04", "Genomför med dialog", "Arbetet planeras och följs upp med en tydlig kontaktväg."],
  ];
  return (
    <section className="section-pad bg-[#087a4d] text-white">
      <div className="container-tisab">
        <p className="eyebrow !text-[#d6f8e5]">Från fråga till färdig plan</p>
        <h2 className="mt-3 max-w-3xl font-heading text-4xl font-extrabold leading-[.95] sm:text-6xl">Fyra tydliga steg. Inga onödiga omvägar.</h2>
        <ol className="mt-10 grid gap-px border border-white/12 bg-white/12 md:grid-cols-4">
          {steps.map(([number, title, text]) => (
            <li key={number} className="bg-white/[.08] p-6 sm:p-8">
              <span className="font-mono text-xs font-bold text-[#d6f8e5]">{number}</span>
              <h3 className="mt-8 font-heading text-xl font-extrabold">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/78">{text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function CustomerPaths({ location }) {
  return (
    <section className="section-pad bg-[#e8f3ed]">
      <div className="container-tisab">
        <p className="eyebrow">Fastigheten avgör vägen</p>
        <h2 className="mt-3 max-w-3xl font-heading text-4xl font-extrabold leading-[.95] text-midnight sm:text-6xl">Vem ansvarar för taket i {location.name}?</h2>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {CUSTOMER_PATHS.map((item) => (
            <Link key={item.title} to={item.to} className="group bg-white p-7 shadow-[0_16px_50px_rgba(6,7,8,.06)]">
              <item.icon className="h-7 w-7 text-tisab" />
              <h3 className="mt-9 font-heading text-2xl font-extrabold text-midnight">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground/70">{item.text}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-tisab">Fortsätt <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
